// ─────────────────────────────────────────────────────────────
//  Protocool – interne Protokollierungs-Maschine (Dashboard-Tab).
//
//  Listet alle Kunden der Sales Pipeline (salesgod-research/customers) als
//  Objekt-Karten. Klick auf einen Kunden oeffnet einen Drawer mit den 4
//  Protokoll-Links. Pro Link: Status, gedroppte Infos (read-only) und ein
//  teilbarer, passwortgeschuetzter Share-Link, den z. B. der Team-Copywriter
//  im Call ausfuellt. Antworten landen am Kundenobjekt und sind hier sichtbar.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ref, onValue } from 'firebase/database';
import { database, auth } from '../firebaseConfig';
import {
  PROTOCOOL_LINKS,
  getProtocoolForm,
  formCompletion,
  isFieldFilled,
  isFieldVisible,
  ratingVerdict,
  checklistScore,
  buildProtocoolShareMessage,
} from '../Protocool/protocoolConfig';
import {
  ACCOUNT_SETUP_PHASES,
  accountSetupCompletion,
  phaseCompletion,
} from '../Protocool/accountSetupConfig';
import { staggerContainer, subtleItemVariants, easeIOS } from './dashboardMotion';
import {
  WARMUP_RAMP_DAYS,
  warmupDayIndex,
  warmupDateKey,
  warmupWorkdayDate,
  warmupDateKeyToMs,
  warmupSubmittedLate,
  fmtWarmupDate,
} from '../Research/warmupUtils';
import './Protocool.css';

const CUSTOMERS_PATH = 'salesgod-research/customers';
const TEAM_PATH = 'salesgod-research/team';
// Launch Phase ("Experimentierphase"): admin-only Knoten, in dem der Outreach-Manager
// täglich seine besten E-Mail-Texte und Erkenntnisse aus dem Research Center ablegt.
const WARMUPS_PATH = 'salesgod-research/warmups';

const SHARE_FN_URL =
  process.env.REACT_APP_PROTOCOOL_SHARE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/setProtocoolShare';

// Account-Setup-Protokoll: Share-Link (Secret Key) generieren + Schritt togglen.
const ACCOUNT_SETUP_SHARE_FN_URL =
  process.env.REACT_APP_ACCOUNT_SETUP_SHARE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/setAccountSetupShare';
const ACCOUNT_SETUP_TOGGLE_FN_URL =
  process.env.REACT_APP_ACCOUNT_SETUP_TOGGLE_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/toggleAccountSetupItem';

// Liefert die Research-Passwoerter (client-unlesbar) ueber die Admin-Function.
const PASSWORDS_FN_URL =
  process.env.REACT_APP_RESEARCH_PASSWORDS_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/getResearchPasswords';

// Launch-Phase-Steuerung (admin-only warmups-Knoten). Wird hier nur zum Zurücksetzen
// einer Launch Phase genutzt (action 'reset' löscht Start, Ziel und alle Tage).
const WARMUP_FN_URL =
  process.env.REACT_APP_RESEARCH_WARMUP_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/submitWarmup';

const resolveIdToken = async () => {
  const user = auth && auth.currentUser;
  return user ? user.getIdToken() : null;
};

const shareUrlFor = (slug, linkKey) =>
  `${window.location.origin}/protocool/${slug}/${linkKey}`;

const accountSetupUrlFor = (slug) => `${window.location.origin}/setup/${slug}`;

const researchUrlFor = (slug) => `${window.location.origin}/research/${slug}`;

// Rollen-Abgleich tolerant gegenüber Alt-/Varianten-Werten: Groß-/Kleinschreibung,
// führende/folgende Leerzeichen sowie Leerzeichen vs. Bindestrich/Unterstrich werden
// vereinheitlicht. So wird z. B. "Outreach Manager" oder "outreach-manager " trotzdem
// als "Outreach-Manager" erkannt (sonst zeigt die Sales Pipeline ihn an, die Setup-
// E-Mails aber "kein Outreach-Manager zugewiesen").
const normalizeRole = (role) =>
  (role == null ? '' : String(role)).trim().toLowerCase().replace(/[\s_-]+/g, '-');
const roleMatches = (role, target) => normalizeRole(role) === normalizeRole(target);

// Liefert pro Link den Zustand fuer einen Kunden: 'filled' | 'shared' | 'empty'.
const linkState = (customer, linkKey) => {
  const node = (customer.protocool && customer.protocool[linkKey]) || {};
  if (node.submittedAt) return 'filled';
  if (node.share && node.share.password) return 'shared';
  return 'empty';
};

const Protocool = () => {
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [openSlug, setOpenSlug] = useState(null);
  // Zwei Ober-Tabs: das bestehende E-Mail-Texting-Protokoll und die Setup-E-Mails
  // (Launch-Phase-Texte, die der Outreach-Manager im Research Center hochlädt).
  const [view, setView] = useState('protocols');

  useEffect(() => {
    const unsub = onValue(ref(database, CUSTOMERS_PATH), (snap) => {
      setCustomers(snap.val() || {});
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const list = useMemo(() => {
    const arr = Object.keys(customers).map((slug) => ({ slug, ...customers[slug] }));
    const q = search.trim().toLowerCase();
    const filtered = q
      ? arr.filter((c) => (c.companyName || '').toLowerCase().includes(q))
      : arr;
    return filtered.sort((a, b) =>
      (a.companyName || '').localeCompare(b.companyName || '', 'en', { sensitivity: 'base' })
    );
  }, [customers, search]);

  const openCustomer = openSlug ? { slug: openSlug, ...(customers[openSlug] || {}) } : null;

  const subByView = view === 'setup'
    ? 'Launch-Phase-Setups aus dem Research Center: Hier laufen alle Texte und Erkenntnisse auf, die der Outreach-Manager täglich hochlädt.'
    : view === 'accountsetup'
      ? 'Account-Setup-Protokoll pro Kunde: teile einen Secret-Key-Link, der Setup-Manager hakt die 3 Phasen ab – live geteilt mit Sales Pipeline & Research Center.'
      : 'Internal protocols per customer. Share a link, your team fills it in the call \u2014 answers land on the customer.';

  return (
    <div className="pc-root">
      <div className="pc-head">
        <div>
          <h1 className="pc-h1">Protocool</h1>
          <p className="pc-sub">{subByView}</p>
        </div>
        <input
          className="pc-search"
          type="text"
          placeholder={view === 'setup' ? 'Kunde suchen…' : 'Search customer…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="pc-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === 'protocols'}
          className={`pc-tab ${view === 'protocols' ? 'pc-tab-active' : ''}`}
          onClick={() => setView('protocols')}
        >
          E-Mail-Texting-Protokoll
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'setup'}
          className={`pc-tab ${view === 'setup' ? 'pc-tab-active' : ''}`}
          onClick={() => setView('setup')}
        >
          Setup E-Mails
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === 'accountsetup'}
          className={`pc-tab ${view === 'accountsetup' ? 'pc-tab-active' : ''}`}
          onClick={() => setView('accountsetup')}
        >
          Account-Setup-Protokoll
        </button>
      </div>

      {view === 'accountsetup' ? (
        <AccountSetupView list={list} loading={loading} />
      ) : view === 'protocols' ? (
        <div className="pc-scroll">
          {loading ? (
            <div className="pc-empty">Loading customers…</div>
          ) : list.length === 0 ? (
            <div className="pc-empty">No customers found.</div>
          ) : (
            <motion.div className="pc-grid" variants={staggerContainer} initial="initial" animate="animate">
              {list.map((c) => {
                const activeLinks = PROTOCOOL_LINKS.filter((l) => l.active);
                const filledCount = activeLinks.filter((l) => linkState(c, l.key) === 'filled').length;
                return (
                  <motion.button
                    key={c.slug}
                    className="pc-card"
                    variants={subtleItemVariants}
                    onClick={() => setOpenSlug(c.slug)}
                  >
                    <div className="pc-card-top">
                      <span className="pc-card-name" title={c.companyName}>{c.companyName || '(no name)'}</span>
                      {c.lite && <span className="pc-card-tag">Lite</span>}
                    </div>
                    {c.industry && <span className="pc-card-industry">{c.industry}</span>}
                    <div className="pc-card-dots">
                      {PROTOCOOL_LINKS.map((l) => (
                        <span
                          key={l.key}
                          className={`pc-dot pc-dot-${linkState(c, l.key)} ${l.active ? '' : 'pc-dot-disabled'}`}
                          title={`${l.label}: ${linkState(c, l.key)}`}
                        />
                      ))}
                    </div>
                    <div className="pc-card-foot">
                      {filledCount > 0
                        ? `${filledCount} protocol${filledCount > 1 ? 's' : ''} filled`
                        : 'No protocols yet'}
                    </div>
                  </motion.button>
                );
              })}
            </motion.div>
          )}
        </div>
      ) : (
        <SetupEmailsView customers={customers} search={search} />
      )}

      <AnimatePresence>
        {view === 'protocols' && openCustomer && (
          <CustomerProtocools
            key={openCustomer.slug}
            customer={openCustomer}
            onClose={() => setOpenSlug(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Setup E-Mails: alle Launch-Phase-Setups (Experimentierphase) ──
// Liest den admin-only warmups-Knoten und bündelt pro Kunde die Texte und
// Erkenntnisse, die der Outreach-Manager im Research Center täglich hochlädt.
const warmupStats = (warmup) => {
  const days = (warmup && warmup.days && typeof warmup.days === 'object') ? warmup.days : {};
  const entries = Object.values(days).filter((d) => d && d.dateKey);
  let sent = 0;
  let positive = 0;
  let texts = 0;
  let lastAt = 0;
  entries.forEach((d) => {
    sent += Number(d.sentEmails) || 0;
    positive += Number(d.positiveReplies) || 0;
    texts += Array.isArray(d.uploadedTexts) ? d.uploadedTexts.length : 0;
    const at = Number(d.updatedAt || d.submittedAt) || 0;
    if (at > lastAt) lastAt = at;
  });
  return { entries, sent, positive, texts, lastAt };
};

// Tag-für-Tag-Disziplin der Launch Phase: prüft jeden fälligen Werktag, ob er
// erledigt (submittedAt), nachgetragen (verspätet), heute noch offen, vergessen
// (überfällig) oder noch nicht fällig ist. Daraus leitet sich der Gesundheits-
// Status ab, damit der Admin sieht, wo es brenzlig wird und wen er erinnern muss.
const warmupHealth = (warmup) => {
  const days = (warmup && warmup.days && typeof warmup.days === 'object') ? warmup.days : {};
  const startAt = warmup && warmup.startAt;
  const result = { items: [], missed: [], lateCount: 0, doneCount: 0, todayOpen: false, unconfirmed: 0, status: 'pending' };
  if (typeof startAt !== 'number' || startAt > Date.now()) return result;

  const todayKeyMs = warmupDateKeyToMs(warmupDateKey(Date.now()));
  for (let d = 1; d <= WARMUP_RAMP_DAYS; d += 1) {
    const dayMs = warmupWorkdayDate(startAt, d);
    const dk = warmupDateKey(dayMs);
    const dayKeyMs = warmupDateKeyToMs(dk);
    const entry = days[dk] || null;
    let status;
    if (entry && entry.submittedAt) {
      status = warmupSubmittedLate(entry) ? 'late' : 'done';
      result.doneCount += 1;
      if (status === 'late') result.lateCount += 1;
      if (!(entry.sentConfirmed || Number(entry.sentEmails) > 0)) result.unconfirmed += 1;
    } else if (dayKeyMs > todayKeyMs) {
      status = 'upcoming';
    } else if (dayKeyMs === todayKeyMs) {
      status = 'today';
      result.todayOpen = true;
    } else {
      status = 'missed';
      result.missed.push({ d, dk, dayMs });
    }
    result.items.push({ d, dk, dayMs, entry, status });
  }

  if (result.missed.length > 0) result.status = 'alert';
  else if (result.todayOpen) result.status = 'due';
  else result.status = 'ok';
  return result;
};

// Initialen aus einem Namen (für das Outreach-Manager-Badge).
const initialsOf = (name) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase() || '?';

const HEALTH_META = {
  pending: { label: 'Startklar', tone: 'pending' },
  ok: { label: 'Auf Kurs', tone: 'ok' },
  due: { label: 'Heute noch offen', tone: 'due' },
  alert: { label: 'Tage vergessen', tone: 'alert' },
};

const DISC_LABEL = {
  done: 'pünktlich erledigt',
  late: 'verspätet nachgetragen',
  today: 'heute fällig – noch offen',
  missed: 'vergessen / überfällig',
  upcoming: 'noch nicht fällig',
};

const SetupEmailsView = ({ customers, search }) => {
  const [warmups, setWarmups] = useState({});
  const [team, setTeam] = useState({});
  const [loading, setLoading] = useState(true);
  const [openSlug, setOpenSlug] = useState(null);

  useEffect(() => {
    const unsubW = onValue(ref(database, WARMUPS_PATH), (snap) => {
      setWarmups(snap.val() || {});
      setLoading(false);
    });
    const unsubT = onValue(ref(database, TEAM_PATH), (snap) => setTeam(snap.val() || {}));
    return () => { unsubW(); unsubT(); };
  }, []);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return Object.keys(warmups)
      .map((slug) => {
        const w = warmups[slug] || {};
        const cust = customers[slug] || {};
        // Zugewiesener Outreach-Manager dieses Projekts (aus der Sales Pipeline).
        const assigned = Object.keys(cust.team || {}).map((id) => team[id]).filter(Boolean);
        const outreachManager = assigned.find((m) => roleMatches(m.role, 'Outreach-Manager')) || null;
        return {
          slug,
          warmup: w,
          companyName: cust.companyName || slug,
          industry: cust.industry || '',
          outreachManager,
        };
      })
      .filter((r) => r.warmup && typeof r.warmup.startAt === 'number' && Number(r.warmup.dailyTarget) > 0)
      .filter((r) => (q ? r.companyName.toLowerCase().includes(q) : true))
      .sort((a, b) => a.companyName.localeCompare(b.companyName, 'en', { sensitivity: 'base' }));
  }, [warmups, customers, team, search]);

  const openRow = openSlug ? list.find((r) => r.slug === openSlug) || null : null;

  const fmtAgo = (ms) => (ms ? new Date(ms).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' }) : '—');

  return (
    <div className="pc-scroll">
      {loading ? (
        <div className="pc-empty">Launch-Phase-Setups werden geladen…</div>
      ) : list.length === 0 ? (
        <div className="pc-empty">
          Noch keine Launch Phase gestartet. Sobald im Research Center eine Launch Phase läuft, erscheinen hier die täglichen Setup-E-Mails.
        </div>
      ) : (
        <motion.div className="pc-grid pc-setup-grid" variants={staggerContainer} initial="initial" animate="animate">
          {list.map((r) => {
            const stats = warmupStats(r.warmup);
            const health = warmupHealth(r.warmup);
            const pending = r.warmup.startAt > Date.now();
            const idx = warmupDayIndex(r.warmup.startAt, Date.now());
            const inRamp = idx >= 1 && idx <= WARMUP_RAMP_DAYS;
            const pct = Math.max(0, Math.min(100, Math.round((Math.min(idx, WARMUP_RAMP_DAYS) / WARMUP_RAMP_DAYS) * 100)));
            const stageLabel = pending ? 'Startklar' : inRamp ? `Tag ${idx} / ${WARMUP_RAMP_DAYS}` : 'Zielbetrieb';
            const meta = HEALTH_META[health.status] || HEALTH_META.ok;
            const healthLabel = health.status === 'alert'
              ? `${health.missed.length} Tag${health.missed.length === 1 ? '' : 'e'} vergessen`
              : meta.label;
            const om = r.outreachManager;
            return (
              <motion.button
                key={r.slug}
                className={`pc-card pc-setup-card pc-setup-card-${meta.tone}`}
                variants={subtleItemVariants}
                onClick={() => setOpenSlug(r.slug)}
              >
                <div className="pc-card-top">
                  <span className="pc-card-name" title={r.companyName}>{r.companyName}</span>
                  <span className={`pc-health-badge pc-health-${meta.tone}`}>
                    <span className="pc-health-dot" />
                    {healthLabel}
                  </span>
                </div>

                <div className="pc-setup-om">
                  {om ? (
                    <>
                      <span className="pc-setup-om-avatar" title={om.name}>{initialsOf(om.name)}</span>
                      <span className="pc-setup-om-text">
                        <span className="pc-setup-om-name">{om.name}</span>
                        <span className="pc-setup-om-role">Outreach-Manager{r.industry ? ` · ${r.industry}` : ''}</span>
                      </span>
                    </>
                  ) : (
                    <span className="pc-setup-om-none">Kein Outreach-Manager zugewiesen</span>
                  )}
                </div>

                <div className="pc-setup-progress">
                  <div className="pc-setup-bar"><span className="pc-setup-bar-fill" style={{ width: `${pct}%` }} /></div>
                  <span className="pc-setup-progress-lbl">{stageLabel}</span>
                </div>

                {/* Tag-für-Tag-Disziplin: erledigt / verspätet / heute offen / vergessen */}
                {health.items.length > 0 && (
                  <div className="pc-disc" aria-label="Tagesstatus">
                    {health.items.map((it) => (
                      <span
                        key={it.d}
                        className={`pc-disc-cell pc-disc-${it.status}`}
                        title={`Tag ${it.d} · ${fmtWarmupDate(it.dk)} · ${DISC_LABEL[it.status]}`}
                      />
                    ))}
                  </div>
                )}

                <div className="pc-setup-stats">
                  <span className="pc-setup-stat"><strong>{health.doneCount}</strong> erledigt</span>
                  <span className="pc-setup-stat"><strong>{stats.texts}</strong> Texte</span>
                  <span className="pc-setup-stat pc-setup-stat-pos"><strong>{stats.positive}</strong> positiv</span>
                </div>

                <div className="pc-card-foot">
                  {health.status === 'alert'
                    ? `Vergessen: ${health.missed.map((m) => `Tag ${m.d}`).join(', ')}`
                    : health.status === 'due'
                      ? 'Heutiger Tag noch nicht eingetragen – ggf. erinnern'
                      : health.lateCount > 0
                        ? `Auf Kurs · ${health.lateCount}× nachgetragen · zuletzt ${fmtAgo(stats.lastAt)}`
                        : `Auf Kurs · zuletzt ${fmtAgo(stats.lastAt)}`}
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      <AnimatePresence>
        {openRow && <SetupDrawer key={openRow.slug} row={openRow} onClose={() => setOpenSlug(null)} />}
      </AnimatePresence>
    </div>
  );
};

// ── Drawer: alle Launch-Phase-Tage eines Kunden (Setup-E-Mails) ──
const SetupDrawer = ({ row, onClose }) => {
  const days = (row.warmup && row.warmup.days && typeof row.warmup.days === 'object') ? row.warmup.days : {};
  const sortedDays = Object.values(days)
    .filter((d) => d && d.dateKey)
    .sort((a, b) => (a.dateKey < b.dateKey ? 1 : -1));
  const stats = warmupStats(row.warmup);
  const health = warmupHealth(row.warmup);
  const meta = HEALTH_META[health.status] || HEALTH_META.ok;
  const om = row.outreachManager;

  // Launch Phase zurücksetzen – bewusst mit doppelter Bestätigung, da der Counter
  // auf 0 gesetzt wird und der Kontakt danach aus den Setup-E-Mails verschwindet.
  const [resetStep, setResetStep] = useState(0); // 0 = aus, 1 = 1. Bestätigung, 2 = 2. Bestätigung
  const [resetting, setResetting] = useState(false);
  const [resetErr, setResetErr] = useState('');

  const doReset = useCallback(async () => {
    if (resetting) return;
    setResetting(true);
    setResetErr('');
    try {
      const token = await resolveIdToken();
      const resp = await fetch(WARMUP_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ slug: row.slug, action: 'reset' }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(d.error || `Zurücksetzen fehlgeschlagen (HTTP ${resp.status}).`);
      }
      // warmups-Knoten ist gelöscht -> der Live-Listener entfernt den Kontakt
      // automatisch aus der Liste. Drawer schließen.
      onClose();
    } catch (e) {
      setResetErr(e.message || 'Zurücksetzen fehlgeschlagen.');
      setResetting(false);
    }
  }, [resetting, row.slug, onClose]);

  const bannerText = health.status === 'alert'
    ? `${health.missed.length} fällige${health.missed.length === 1 ? 'r' : ''} Tag${health.missed.length === 1 ? '' : 'e'} nicht eingetragen: ${health.missed.map((m) => `Tag ${m.d} (${fmtWarmupDate(m.dk)})`).join(', ')}. Bitte den Outreach-Manager erinnern.`
    : health.status === 'due'
      ? 'Der heutige Tag ist noch offen. Wenn er heute nicht eingetragen wird, gilt er morgen als verpasst.'
      : health.status === 'pending'
        ? 'Die Launch Phase ist startklar, aber noch nicht losgelaufen.'
        : health.lateCount > 0
          ? `Alle fälligen Tage erledigt – ${health.lateCount} davon wurden nachgetragen.`
          : 'Alle fälligen Tage pünktlich erledigt. Sauber dran.';

  const fmtTime = (ms) =>
    ms ? new Date(ms).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <>
      <motion.div
        className="pc-drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.aside
        className="pc-drawer"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.32, ease: easeIOS }}
      >
        <header className="pc-drawer-head">
          <div className="pc-drawer-title-wrap">
            <span className="pc-drawer-badge">Setup E-Mails · Launch Phase</span>
            <h2 className="pc-drawer-title">{row.companyName}</h2>
          </div>
          <button className="pc-drawer-close" onClick={onClose} aria-label="Schließen">×</button>
        </header>

        <div className="pc-drawer-body">
          {/* Verantwortlicher Outreach-Manager (aus der Sales Pipeline) */}
          <div className="pc-setup-owner">
            <span className="pc-setup-owner-label">Verantwortlich</span>
            {om ? (
              <div className="pc-setup-owner-card">
                <span className="pc-setup-om-avatar pc-setup-om-avatar-lg">{initialsOf(om.name)}</span>
                <div className="pc-setup-owner-text">
                  <span className="pc-setup-owner-name">{om.name}</span>
                  <span className="pc-setup-owner-role">Outreach-Manager</span>
                </div>
                {om.email && (
                  <a className="pc-mini-btn" href={`mailto:${om.email}`} onClick={(e) => e.stopPropagation()}>
                    Erinnern
                  </a>
                )}
              </div>
            ) : (
              <div className="pc-setup-owner-card">
                <span className="pc-setup-om-none">Kein Outreach-Manager in der Sales Pipeline zugewiesen.</span>
              </div>
            )}
          </div>

          {/* Gesundheits-Banner: alles gut, heute offen oder Tage vergessen */}
          <div className={`pc-health-banner pc-health-${meta.tone}`}>
            <span className="pc-health-dot" />
            <span className="pc-health-banner-text">{bannerText}</span>
          </div>

          {/* Tag-für-Tag-Disziplin über die gesamte Launch Phase */}
          {health.items.length > 0 && (
            <div className="pc-disc-block">
              <div className="pc-disc-strip">
                {health.items.map((it) => (
                  <span
                    key={it.d}
                    className={`pc-disc-cell pc-disc-lg pc-disc-${it.status}`}
                    title={`Tag ${it.d} · ${fmtWarmupDate(it.dk)} · ${DISC_LABEL[it.status]}`}
                  >
                    {it.d}
                  </span>
                ))}
              </div>
              <div className="pc-disc-legend">
                <span className="pc-disc-leg"><span className="pc-disc-cell pc-disc-done" />erledigt</span>
                <span className="pc-disc-leg"><span className="pc-disc-cell pc-disc-late" />nachgetragen</span>
                <span className="pc-disc-leg"><span className="pc-disc-cell pc-disc-today" />heute offen</span>
                <span className="pc-disc-leg"><span className="pc-disc-cell pc-disc-missed" />vergessen</span>
                <span className="pc-disc-leg"><span className="pc-disc-cell pc-disc-upcoming" />offen</span>
              </div>
            </div>
          )}

          <div className="pc-setup-summary">
            <div className="pc-setup-sum"><strong>{stats.texts}</strong><span>E-Mail-Texte</span></div>
            <div className="pc-setup-sum"><strong>{stats.sent}</strong><span>gesendet</span></div>
            <div className="pc-setup-sum pc-setup-sum-pos"><strong>{stats.positive}</strong><span>positive Antworten</span></div>
            <div className="pc-setup-sum"><strong>{sortedDays.length}</strong><span>Tage dokumentiert</span></div>
          </div>

          {sortedDays.length === 0 ? (
            <p className="pc-responses-empty">
              Der Outreach-Manager hat in dieser Launch Phase noch keine Texte oder Erkenntnisse hochgeladen.
            </p>
          ) : (
            <div className="pc-setup-days">
              {sortedDays.map((d) => {
                const texts = Array.isArray(d.uploadedTexts)
                  ? d.uploadedTexts.filter((t) => t && ((t.subject || '').trim() || (t.body || '').trim()))
                  : [];
                const quests = Array.isArray(d.tomorrowQuest)
                  ? d.tomorrowQuest.filter((q) => q && (q.text || '').trim())
                  : [];
                const hasRemarks = (d.remarks || '').trim().length > 0;
                return (
                  <section key={d.dateKey} className="pc-setup-day">
                    <header className="pc-setup-day-head">
                      <div className="pc-setup-day-title">
                        <span className="pc-setup-day-no">{d.dayIndex ? `Tag ${d.dayIndex}` : 'Launch Phase'}</span>
                        <span className="pc-setup-day-date">{fmtWarmupDate(d.dateKey)}</span>
                      </div>
                      <div className="pc-setup-day-meta">
                        {d.submittedBy ? `${d.submittedBy} · ` : ''}{fmtTime(d.updatedAt || d.submittedAt)}
                      </div>
                    </header>

                    <div className="pc-setup-day-chips">
                      {warmupSubmittedLate(d) && (
                        <span className="pc-resp-choice pc-resp-mid">verspätet nachgetragen</span>
                      )}
                      {(d.sentConfirmed || Number(d.sentEmails) > 0) ? (
                        <span className="pc-resp-choice pc-resp-good">
                          {Number(d.sentEmails) > 0 ? `${d.sentEmails} gesendet` : 'Versand bestätigt'}
                        </span>
                      ) : (
                        <span className="pc-resp-choice pc-resp-bad">Versand nicht bestätigt</span>
                      )}
                      {Number(d.positiveReplies) > 0 && (
                        <span className="pc-resp-choice">
                          {d.positiveReplies} positive Antwort{Number(d.positiveReplies) === 1 ? '' : 'en'}
                        </span>
                      )}
                    </div>

                    {hasRemarks && (
                      <div className="pc-setup-block">
                        <div className="pc-setup-block-label">Erkenntnisse</div>
                        <div className="pc-resp-text pc-setup-remarks">{d.remarks}</div>
                      </div>
                    )}

                    {texts.length > 0 && (
                      <div className="pc-setup-block">
                        <div className="pc-setup-block-label">E-Mail-Texte ({texts.length})</div>
                        <div className="pc-resp-emails">
                          {texts.map((t, i) => (
                            <div key={t.id || i} className="pc-resp-email">
                              <div className="pc-resp-email-subject">{t.subject || '(kein Betreff)'}</div>
                              {t.body && <div className="pc-resp-email-body">{t.body}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {quests.length > 0 && (
                      <div className="pc-setup-block">
                        <div className="pc-setup-block-label">Aufgaben für morgen</div>
                        <ul className="pc-setup-quests">
                          {quests.map((q, i) => (
                            <li key={i} className={`pc-setup-quest ${q.done ? 'is-done' : ''}`}>{q.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {texts.length === 0 && !hasRemarks && quests.length === 0 && (
                      <p className="pc-setup-empty-day">Keine Texte oder Notizen an diesem Tag.</p>
                    )}
                  </section>
                );
              })}
            </div>
          )}

          {/* Gefahrenzone: Launch Phase zurücksetzen (doppelte Bestätigung) */}
          <div className="pc-setup-danger">
            <div className="pc-setup-danger-head">
              <span className="pc-setup-danger-title">Launch Phase zurücksetzen</span>
              <span className="pc-setup-danger-sub">
                Setzt den Counter auf 0, löscht alle dokumentierten Tage und Texte und entfernt den Kontakt aus den Setup-E-Mails. Nicht umkehrbar.
              </span>
            </div>

            {resetStep === 0 && (
              <button type="button" className="pc-mini-btn pc-mini-btn-danger" onClick={() => { setResetErr(''); setResetStep(1); }}>
                Launch Phase zurücksetzen
              </button>
            )}

            {resetStep === 1 && (
              <div className="pc-setup-danger-confirm">
                <span className="pc-setup-danger-q">Wirklich zurücksetzen? Alle {sortedDays.length} dokumentierten Tage gehen verloren.</span>
                <div className="pc-setup-danger-actions">
                  <button type="button" className="pc-mini-btn pc-mini-btn-ghost" onClick={() => setResetStep(0)}>Abbrechen</button>
                  <button type="button" className="pc-mini-btn pc-mini-btn-danger" onClick={() => setResetStep(2)}>Weiter</button>
                </div>
              </div>
            )}

            {resetStep === 2 && (
              <div className="pc-setup-danger-confirm">
                <span className="pc-setup-danger-q pc-setup-danger-q-final">
                  Letzte Bestätigung für <strong>{row.companyName}</strong>: endgültig und unwiderruflich zurücksetzen?
                </span>
                <div className="pc-setup-danger-actions">
                  <button type="button" className="pc-mini-btn pc-mini-btn-ghost" onClick={() => setResetStep(0)} disabled={resetting}>Abbrechen</button>
                  <button type="button" className="pc-mini-btn pc-mini-btn-danger" onClick={doReset} disabled={resetting}>
                    {resetting ? 'Wird zurückgesetzt…' : 'Endgültig zurücksetzen'}
                  </button>
                </div>
              </div>
            )}

            {resetErr && <div className="pc-share-error">{resetErr}</div>}
          </div>
        </div>
      </motion.aside>
    </>
  );
};

// ── Drawer: alle Protokolle eines Kunden ─────────────────────────
const CustomerProtocools = ({ customer, onClose }) => {
  const [activeLinkKey, setActiveLinkKey] = useState('brainstorming');
  const activeLink = PROTOCOOL_LINKS.find((l) => l.key === activeLinkKey) || PROTOCOOL_LINKS[0];

  return (
    <>
      <motion.div
        className="pc-drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.aside
        className="pc-drawer"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.32, ease: easeIOS }}
      >
        <header className="pc-drawer-head">
          <div className="pc-drawer-title-wrap">
            <span className="pc-drawer-badge">Protocool</span>
            <h2 className="pc-drawer-title">{customer.companyName || '(no name)'}</h2>
          </div>
          <button className="pc-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <nav className="pc-drawer-tabs">
          {PROTOCOOL_LINKS.map((l) => (
            <button
              key={l.key}
              className={`pc-drawer-tab ${activeLinkKey === l.key ? 'pc-drawer-tab-active' : ''} ${l.active ? '' : 'pc-drawer-tab-soon'}`}
              onClick={() => setActiveLinkKey(l.key)}
            >
              <span className="pc-drawer-tab-no">{l.order}</span>
              <span className="pc-drawer-tab-label">{l.label}</span>
            </button>
          ))}
        </nav>

        <div className="pc-drawer-body">
          <QuickShare customer={customer} />
          {activeLink.active ? (
            <LinkPanel customer={customer} link={activeLink} />
          ) : (
            <div className="pc-coming">
              <div className="pc-coming-icon">{activeLink.order}</div>
              <h3 className="pc-coming-title">{activeLink.label}</h3>
              <p className="pc-coming-text">This protocol is coming soon.</p>
            </div>
          )}
        </div>
      </motion.aside>
    </>
  );
};

// ── Quick-Versand: beide Links generieren + fertige Nachrichten ──
const QuickShare = ({ customer }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ready, setReady] = useState(false);
  const [keys, setKeys] = useState({ brainstorming: '', outreach_prep: '' });
  const [researchKey, setResearchKey] = useState('');
  const [copied, setCopied] = useState('');
  const [team, setTeam] = useState({});

  const reportSlug = customer.reportSlug || customer.slug;

  // Globale Team-Mitglieder live, um Outreach-Manager / Team-Copywriter dieses
  // Projekts automatisch anhand ihrer Position zu finden.
  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => setTeam(snap.val() || {}));
    return () => unsub();
  }, []);
  const assignedMembers = Object.keys(customer.team || {}).map((id) => team[id]).filter(Boolean);
  const firstByRole = (role) => assignedMembers.find((m) => roleMatches(m.role, role)) || null;
  const outreachManager = firstByRole('Outreach-Manager');
  const teamCopywriter = firstByRole('Team-Copywriter');

  const callMs = typeof customer.detailingCallAt === 'number' ? customer.detailingCallAt : customer.meetingAt;
  const defaultCallDate = callMs
    ? new Date(callMs).toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '';
  const defaultDeadline = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  })();

  const [callDate, setCallDate] = useState(defaultCallDate);
  const [deadline, setDeadline] = useState(defaultDeadline);
  const [outreachName, setOutreachName] = useState('');
  const [copywriterName, setCopywriterName] = useState('');
  const [copywriterEmail, setCopywriterEmail] = useState('');
  const [touched, setTouched] = useState({});

  // Namen/Email aus den zugewiesenen Team-Mitgliedern vorbefuellen (nur solange
  // das Feld nicht manuell angefasst wurde – Tippen wird nie ueberschrieben).
  useEffect(() => {
    if (!touched.outreach && outreachManager && outreachManager.name) setOutreachName(outreachManager.name);
    if (!touched.copywriter && teamCopywriter && teamCopywriter.name) setCopywriterName(teamCopywriter.name);
    if (!touched.copywriterMail && teamCopywriter && teamCopywriter.email) setCopywriterEmail(teamCopywriter.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachManager && outreachManager.name, teamCopywriter && teamCopywriter.name, teamCopywriter && teamCopywriter.email]);

  const generate = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      const token = await resolveIdToken();
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

      const shareFor = async (linkKey) => {
        const resp = await fetch(SHARE_FN_URL, {
          method: 'POST',
          headers,
          body: JSON.stringify({ slug: customer.slug, linkKey, regenerate: false }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status !== 200 || !data.ok) throw new Error(data.error || 'Link konnte nicht erstellt werden.');
        return data.password;
      };

      const [bPw, oPw] = await Promise.all([shareFor('brainstorming'), shareFor('outreach_prep')]);
      setKeys({ brainstorming: bPw, outreach_prep: oPw });

      // Research-Passwort (best-effort – Nachricht funktioniert auch ohne).
      try {
        const presp = await fetch(PASSWORDS_FN_URL, { method: 'POST', headers, body: '{}' });
        const pdata = await presp.json().catch(() => ({}));
        if (presp.status === 200 && pdata.ok && pdata.passwords) {
          setResearchKey(String(pdata.passwords[reportSlug] || ''));
        }
      } catch (e) { /* Research-Key optional */ }

      setReady(true);
    } catch (e) {
      setErr(e.message || 'Etwas ist schiefgelaufen.');
    } finally {
      setBusy(false);
    }
  }, [busy, customer.slug, reportSlug]);

  const baseMsg = {
    business: customer.companyName || '',
    researchUrl: researchUrlFor(reportSlug),
    researchKey,
    researchEmailUrl: `${researchUrlFor(reportSlug)}?tab=emails`,
    feedbackDeadline: deadline,
    callDate,
  };

  const copywriterMsg = buildProtocoolShareMessage({
    ...baseMsg,
    otherName: outreachName,
    protocolUrl: shareUrlFor(customer.slug, 'brainstorming'),
    protocolKey: keys.brainstorming,
  });
  const outreachMsg = buildProtocoolShareMessage({
    ...baseMsg,
    otherName: copywriterName,
    protocolUrl: shareUrlFor(customer.slug, 'outreach_prep'),
    protocolKey: keys.outreach_prep,
    nextSteps: { copywriterName, copywriterEmail },
  });

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) { /* clipboard evtl. blockiert */ }
  };

  return (
    <div className="pc-quick">
      <button
        className={`pc-quick-toggle ${open ? 'pc-quick-toggle-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="pc-quick-toggle-icon" aria-hidden>⚡</span>
        <span className="pc-quick-toggle-label">Quick-Versand · beide Links & Nachrichten</span>
        <span className="pc-quick-toggle-chev">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="pc-quick-body">
          {!ready ? (
            <div className="pc-quick-intro">
              <p className="pc-quick-intro-text">
                Erzeugt beide Share-Links (Team-Copywriter & Outreach-Manager) und baut zwei
                fertige Nachrichten zum Kopieren.
              </p>
              <button className="pc-mini-btn pc-mini-btn-primary" onClick={generate} disabled={busy}>
                {busy ? 'Wird erstellt…' : 'Links & Nachrichten erstellen'}
              </button>
              {err && <div className="pc-share-error">{err}</div>}
            </div>
          ) : (
            <>
              <div className="pc-quick-fields">
                <label className="pc-quick-field">
                  <span className="pc-quick-field-label">Datum des Calls</span>
                  <input className="pc-quick-input" value={callDate} onChange={(e) => setCallDate(e.target.value)} placeholder="TT.MM.JJJJ HH:MM" />
                </label>
                <label className="pc-quick-field">
                  <span className="pc-quick-field-label">Feedback-Frist (Research)</span>
                  <input className="pc-quick-input" value={deadline} onChange={(e) => setDeadline(e.target.value)} placeholder="TT.MM.JJJJ" />
                </label>
                <label className="pc-quick-field">
                  <span className="pc-quick-field-label">Name Outreach-Manager</span>
                  <input className="pc-quick-input" value={outreachName} onChange={(e) => { setOutreachName(e.target.value); setTouched((t) => ({ ...t, outreach: true })); }} placeholder="z. B. Lisa" />
                </label>
                <label className="pc-quick-field">
                  <span className="pc-quick-field-label">Name Team-Copywriter</span>
                  <input className="pc-quick-input" value={copywriterName} onChange={(e) => { setCopywriterName(e.target.value); setTouched((t) => ({ ...t, copywriter: true })); }} placeholder="z. B. Max" />
                </label>
                <label className="pc-quick-field">
                  <span className="pc-quick-field-label">E-Mail Team-Copywriter (für NEXT STEPS)</span>
                  <input className="pc-quick-input" value={copywriterEmail} onChange={(e) => { setCopywriterEmail(e.target.value); setTouched((t) => ({ ...t, copywriterMail: true })); }} placeholder="name@firma.com" />
                </label>
              </div>
              {!researchKey && (
                <div className="pc-quick-warn">Research-Key nicht gefunden – im Text als {'{KEY}'} markiert. Bitte manuell ergänzen.</div>
              )}

              <div className="pc-quick-msg">
                <div className="pc-quick-msg-head">
                  <span className="pc-quick-msg-title">Für den Team-Copywriter</span>
                  <button className="pc-mini-btn pc-mini-btn-primary" onClick={() => copy(copywriterMsg, 'cw')}>
                    {copied === 'cw' ? 'Kopiert ✓' : 'Nachricht kopieren'}
                  </button>
                </div>
                <pre className="pc-quick-msg-text">{copywriterMsg}</pre>
              </div>

              <div className="pc-quick-msg">
                <div className="pc-quick-msg-head">
                  <span className="pc-quick-msg-title">Für den Outreach-Manager</span>
                  <button className="pc-mini-btn pc-mini-btn-primary" onClick={() => copy(outreachMsg, 'om')}>
                    {copied === 'om' ? 'Kopiert ✓' : 'Nachricht kopieren'}
                  </button>
                </div>
                <pre className="pc-quick-msg-text">{outreachMsg}</pre>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Panel fuer einen aktiven Link (Share + gedroppte Infos) ──────
const LinkPanel = ({ customer, link }) => {
  const form = getProtocoolForm(link.key);
  const node = (customer.protocool && customer.protocool[link.key]) || {};
  const responses = node.responses || {};
  const completion = formCompletion(form, responses);

  const [password, setPassword] = useState((node.share && node.share.password) || '');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [err, setErr] = useState('');

  // Passwort aus den (live aktualisierten) Kundendaten uebernehmen.
  useEffect(() => {
    setPassword((node.share && node.share.password) || '');
  }, [node.share]);

  const url = shareUrlFor(customer.slug, link.key);

  const ensureShare = useCallback(
    async (regenerate) => {
      if (generating) return;
      setGenerating(true);
      setErr('');
      try {
        const token = await resolveIdToken();
        const resp = await fetch(SHARE_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ slug: customer.slug, linkKey: link.key, regenerate: !!regenerate }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status === 200 && data.ok) {
          setPassword(data.password);
        } else {
          setErr(data.error || 'Could not create share link.');
        }
      } catch (e) {
        setErr('Network error. Please try again.');
      } finally {
        setGenerating(false);
      }
    },
    [customer.slug, link.key, generating]
  );

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) { /* clipboard kann blockiert sein – still ignorieren */ }
  };

  const fmtDate = (ms) =>
    ms ? new Date(ms).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  const renderValue = (field) => {
    const v = responses[field.id];
    if (!isFieldFilled(v)) return <span className="pc-resp-empty">— not answered</span>;
    if (field.type === 'rating') {
      const max = field.max || 10;
      const verdict = ratingVerdict(v, max, field.verdictVariant);
      return (
        <span className="pc-resp-rating-wrap">
          <span className="pc-resp-rating">{v}<span className="pc-resp-rating-max"> / {max}</span></span>
          {verdict && <span className={`pc-resp-choice pc-resp-${verdict.tone}`}>{verdict.label}</span>}
        </span>
      );
    }
    if (field.type === 'tier') {
      const opt = (field.options || []).find((o) => o.value === v);
      const tone = opt && opt.tone ? `pc-resp-${opt.tone}` : '';
      return (
        <span className={`pc-resp-choice ${tone}`}>
          {opt ? `${opt.value} · ${opt.label}` : v}
        </span>
      );
    }
    if (field.type === 'checklist') {
      const opts = field.options || [];
      const selected = Array.isArray(v) ? v : [];
      const score = checklistScore(field, v);
      return (
        <span className="pc-resp-checklist">
          {opts
            .filter((o) => selected.includes(o.value))
            .map((o) => (
              <span key={o.value} className="pc-resp-choice pc-resp-good">{o.label}</span>
            ))}
          {score.maxPoints > 0 && (
            <span className="pc-resp-points">{score.points} / {score.maxPoints} pts</span>
          )}
        </span>
      );
    }
    if (field.type === 'protocol') {
      const p = v && typeof v === 'object' ? v : null;
      if (!p) return <span className="pc-resp-empty">— not answered</span>;
      if (p.mode === 'file' && p.file && p.file.url) {
        return (
          <a className="pc-resp-protocol-link" href={p.file.url} target="_blank" rel="noopener noreferrer">
            {p.file.name || 'Download protocol'}
          </a>
        );
      }
      if (p.mode === 'text' && (p.text || '').trim()) {
        return <span className="pc-resp-text">{p.text}</span>;
      }
      return <span className="pc-resp-empty">— not answered</span>;
    }
    if (field.type === 'emails') {
      const texts = (Array.isArray(v) ? v : []).filter(
        (t) => t && ((t.subject || '').trim() || (t.body || '').trim())
      );
      if (!texts.length) return <span className="pc-resp-empty">— not answered</span>;
      return (
        <div className="pc-resp-emails">
          {texts.map((t, i) => (
            <div key={t.id || i} className="pc-resp-email">
              <div className="pc-resp-email-subject">{t.subject || '(no subject)'}</div>
              {t.body && <div className="pc-resp-email-body">{t.body}</div>}
            </div>
          ))}
        </div>
      );
    }
    if (field.type === 'choice' || field.type === 'segment') {
      const opt = (field.options || []).find((o) => o.value === v);
      const tone = opt && opt.tone ? `pc-resp-${opt.tone}` : '';
      return <span className={`pc-resp-choice ${tone}`}>{opt ? opt.label : v}</span>;
    }
    return <span className="pc-resp-text">{String(v)}</span>;
  };

  return (
    <div className="pc-panel">
      <div className="pc-panel-head">
        <div className="pc-panel-head-main">
          <span className="pc-panel-stage">{link.stage}</span>
          <h3 className="pc-panel-title">{link.label}</h3>
          <p className="pc-panel-sub">{link.subtitle}</p>
          <div className="pc-panel-meta">
            {link.runBy && <span className="pc-panel-chip">Run by: {link.runBy}</span>}
            {link.audience && <span className="pc-panel-chip pc-panel-chip-audience">Filled by: {link.audience}</span>}
          </div>
        </div>
        <div className={`pc-status pc-status-${linkState(customer, link.key)}`}>
          {node.submittedAt ? `Filled \u00b7 ${completion.filled}/${completion.total}` : password ? 'Shared' : 'Not started'}
        </div>
      </div>

      {/* How to run this – Ablauf fuer die Team-Leiterin */}
      {Array.isArray(link.instructions) && link.instructions.length > 0 && (
        <div className="pc-howto">
          <div className="pc-howto-title">How to run this</div>
          <ol className="pc-howto-steps">
            {link.instructions.map((step, i) => (
              <li key={i} className="pc-howto-step">
                <span className="pc-howto-num">{i + 1}</span>
                <span className="pc-howto-text">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Share-Bereich */}
      <div className="pc-share">
        <div className="pc-share-title">Step 1 \u00b7 Shareable link</div>
        {password ? (
          <>
            <div className="pc-share-row">
              <span className="pc-share-label">Link</span>
              <code className="pc-share-value">{url}</code>
              <button className="pc-mini-btn" onClick={() => copy(url, 'url')}>
                {copied === 'url' ? 'Copied \u2713' : 'Copy'}
              </button>
              <a className="pc-mini-btn" href={url} target="_blank" rel="noopener noreferrer">Open</a>
            </div>
            <div className="pc-share-row">
              <span className="pc-share-label">Password</span>
              <code className="pc-share-value pc-share-pw">{password}</code>
              <button className="pc-mini-btn" onClick={() => copy(password, 'pw')}>
                {copied === 'pw' ? 'Copied \u2713' : 'Copy'}
              </button>
            </div>
            <div className="pc-share-actions">
              <button
                className="pc-mini-btn pc-mini-btn-primary"
                onClick={() => copy(`${url}\nPassword: ${password}`, 'both')}
              >
                {copied === 'both' ? 'Copied \u2713' : 'Copy link + password'}
              </button>
              <button className="pc-mini-btn pc-mini-btn-ghost" onClick={() => ensureShare(true)} disabled={generating}>
                {generating ? 'Working\u2026' : 'Regenerate password'}
              </button>
            </div>
          </>
        ) : (
          <div className="pc-share-empty">
            <p className="pc-share-empty-text">No share link yet. Generate a password to share this protocol.</p>
            <button className="pc-mini-btn pc-mini-btn-primary" onClick={() => ensureShare(false)} disabled={generating}>
              {generating ? 'Generating\u2026' : 'Generate share link'}
            </button>
          </div>
        )}
        {err && <div className="pc-share-error">{err}</div>}
      </div>

      {/* Gedroppte Infos */}
      <div className="pc-responses">
        <div className="pc-responses-head">
          <span className="pc-responses-title">Step 2 \u00b7 Dropped information</span>
          {node.submittedAt && (
            <span className="pc-responses-meta">
              {node.filledBy ? `by ${node.filledBy} \u00b7 ` : ''}{fmtDate(node.updatedAt || node.submittedAt)}
            </span>
          )}
        </div>

        {!node.submittedAt && completion.filled === 0 ? (
          <p className="pc-responses-empty">Nothing dropped yet. Once the link is filled out, the info shows up here.</p>
        ) : (
          form.sections.map((section) => (
            <div key={section.id} className="pc-resp-section">
              <div className="pc-resp-section-title">{section.title}</div>
              {(section.fields || [])
                .filter((field) => isFieldVisible(field, responses))
                .map((field) => (
                  <div key={field.id} className="pc-resp-item">
                    <div className="pc-resp-q">{field.label}</div>
                    <div className="pc-resp-a">{renderValue(field)}</div>
                  </div>
                ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ── Account-Setup-Protokoll: Kunden-Grid + Share/Progress-Drawer ──
// Liest die GETEILTE Checkliste (customer.checklist) – dieselbe Quelle wie die
// Sales Pipeline (Setup-Schritt) und die Research-Seite (Setup-Tab). Pro Kunde
// ein Secret-Key-Link (/setup/<slug>), den der Setup-Manager ausfüllt.
const AccountSetupView = ({ list, loading }) => {
  const [openSlug, setOpenSlug] = useState(null);
  const openRow = openSlug ? list.find((c) => c.slug === openSlug) || null : null;

  return (
    <div className="pc-scroll">
      {loading ? (
        <div className="pc-empty">Loading customers…</div>
      ) : list.length === 0 ? (
        <div className="pc-empty">No customers found.</div>
      ) : (
        <motion.div className="pc-grid" variants={staggerContainer} initial="initial" animate="animate">
          {list.map((c) => {
            const comp = accountSetupCompletion(c.checklist);
            const shared = !!(c.accountSetup && c.accountSetup.share && c.accountSetup.share.password);
            const tone = comp.pct === 100 ? 'ok' : comp.done > 0 ? 'due' : 'pending';
            return (
              <motion.button
                key={c.slug}
                className={`pc-card pc-as-card pc-as-card-${tone}`}
                variants={subtleItemVariants}
                onClick={() => setOpenSlug(c.slug)}
              >
                <div className="pc-card-top">
                  <span className="pc-card-name" title={c.companyName}>{c.companyName || '(no name)'}</span>
                  <span className={`pc-as-badge pc-as-badge-${shared ? 'shared' : 'empty'}`}>
                    {shared ? 'geteilt' : 'kein Link'}
                  </span>
                </div>
                {c.industry && <span className="pc-card-industry">{c.industry}</span>}
                <div className="pc-as-progress">
                  <div className="pc-as-bar"><span className="pc-as-bar-fill" style={{ width: `${comp.pct}%` }} /></div>
                  <span className="pc-as-progress-lbl">{comp.done}/{comp.total}</span>
                </div>
                <div className="pc-card-foot">
                  {comp.pct === 100
                    ? 'Setup komplett ✓'
                    : comp.done > 0
                      ? `${comp.pct}% erledigt`
                      : 'Setup noch nicht gestartet'}
                </div>
              </motion.button>
            );
          })}
        </motion.div>
      )}

      <AnimatePresence>
        {openRow && <AccountSetupDrawer key={openRow.slug} customer={openRow} onClose={() => setOpenSlug(null)} />}
      </AnimatePresence>
    </div>
  );
};

const AccountSetupDrawer = ({ customer, onClose }) => {
  const checklist = customer.checklist || {};
  const comp = accountSetupCompletion(checklist);
  const shareNode = (customer.accountSetup && customer.accountSetup.share) || {};

  const [password, setPassword] = useState(shareNode.password || '');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [err, setErr] = useState('');
  const [busyItem, setBusyItem] = useState('');

  const url = accountSetupUrlFor(customer.slug);

  useEffect(() => {
    setPassword((customer.accountSetup && customer.accountSetup.share && customer.accountSetup.share.password) || '');
  }, [customer.accountSetup]);

  const ensureShare = useCallback(
    async (regenerate) => {
      if (generating) return;
      setGenerating(true);
      setErr('');
      try {
        const token = await resolveIdToken();
        const resp = await fetch(ACCOUNT_SETUP_SHARE_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ slug: customer.slug, regenerate: !!regenerate }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status === 200 && data.ok) setPassword(data.password);
        else setErr(data.error || 'Link konnte nicht erstellt werden.');
      } catch (e) {
        setErr('Netzwerkfehler. Bitte versuch es erneut.');
      } finally {
        setGenerating(false);
      }
    },
    [customer.slug, generating]
  );

  const toggleStep = useCallback(
    async (stepId, nextDone) => {
      if (busyItem) return;
      setBusyItem(stepId);
      setErr('');
      try {
        const token = await resolveIdToken();
        const resp = await fetch(ACCOUNT_SETUP_TOGGLE_FN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ slug: customer.slug, itemId: stepId, done: nextDone, by: 'admin' }),
        });
        const data = await resp.json().catch(() => ({}));
        if (resp.status !== 200 || !data.ok) setErr(data.error || 'Konnte nicht speichern.');
        // Der Live-Listener (customers) aktualisiert die Anzeige automatisch.
      } catch (e) {
        setErr('Netzwerkfehler beim Speichern.');
      } finally {
        setBusyItem('');
      }
    },
    [customer.slug, busyItem]
  );

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
      setTimeout(() => setCopied(''), 1800);
    } catch (e) { /* clipboard evtl. blockiert */ }
  };

  const lastUpdate = customer.accountSetup && typeof customer.accountSetup.updatedAt === 'number'
    ? new Date(customer.accountSetup.updatedAt).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <>
      <motion.div
        className="pc-drawer-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={onClose}
      />
      <motion.aside
        className="pc-drawer"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ duration: 0.32, ease: easeIOS }}
      >
        <header className="pc-drawer-head">
          <div className="pc-drawer-title-wrap">
            <span className="pc-drawer-badge">Account-Setup-Protokoll</span>
            <h2 className="pc-drawer-title">{customer.companyName || '(no name)'}</h2>
          </div>
          <button className="pc-drawer-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="pc-drawer-body">
          {/* Gesamtfortschritt */}
          <div className="pc-as-summary">
            <div className="pc-as-summary-head">
              <span className="pc-as-summary-title">Setup-Fortschritt</span>
              <span className={`pc-as-summary-prog ${comp.pct === 100 ? 'is-ok' : ''}`}>{comp.done}/{comp.total} · {comp.pct}%</span>
            </div>
            <div className="pc-as-bar pc-as-bar-lg"><span className="pc-as-bar-fill" style={{ width: `${comp.pct}%` }} /></div>
            {lastUpdate && <span className="pc-as-summary-meta">Zuletzt {lastUpdate}{customer.accountSetup.lastBy ? ` · ${customer.accountSetup.lastBy}` : ''}</span>}
          </div>

          {/* Share-Bereich (Secret Key) */}
          <div className="pc-share">
            <div className="pc-share-title">Secret-Key-Link · für den Setup-Manager</div>
            {password ? (
              <>
                <div className="pc-share-row">
                  <span className="pc-share-label">Link</span>
                  <code className="pc-share-value">{url}</code>
                  <button className="pc-mini-btn" onClick={() => copy(url, 'url')}>{copied === 'url' ? 'Kopiert ✓' : 'Kopieren'}</button>
                  <a className="pc-mini-btn" href={url} target="_blank" rel="noopener noreferrer">Öffnen</a>
                </div>
                <div className="pc-share-row">
                  <span className="pc-share-label">Secret Key</span>
                  <code className="pc-share-value pc-share-pw">{password}</code>
                  <button className="pc-mini-btn" onClick={() => copy(password, 'pw')}>{copied === 'pw' ? 'Kopiert ✓' : 'Kopieren'}</button>
                </div>
                <div className="pc-share-actions">
                  <button className="pc-mini-btn pc-mini-btn-primary" onClick={() => copy(`${url}\nSecret Key: ${password}`, 'both')}>
                    {copied === 'both' ? 'Kopiert ✓' : 'Link + Key kopieren'}
                  </button>
                  <button className="pc-mini-btn pc-mini-btn-ghost" onClick={() => ensureShare(true)} disabled={generating}>
                    {generating ? 'Arbeite…' : 'Key neu generieren'}
                  </button>
                </div>
              </>
            ) : (
              <div className="pc-share-empty">
                <p className="pc-share-empty-text">Noch kein Link. Generiere einen Secret Key, um das Setup-Protokoll zu teilen.</p>
                <button className="pc-mini-btn pc-mini-btn-primary" onClick={() => ensureShare(false)} disabled={generating}>
                  {generating ? 'Generiere…' : 'Secret-Key-Link erstellen'}
                </button>
              </div>
            )}
            {err && <div className="pc-share-error">{err}</div>}
          </div>

          {/* Phasen-Fortschritt (live, vom Admin direkt abhakbar) */}
          {ACCOUNT_SETUP_PHASES.map((ph, idx) => {
            const pc = phaseCompletion(ph, checklist);
            const phaseDone = pc.total > 0 && pc.done === pc.total;
            return (
              <div key={ph.id} className="pc-as-phase" style={{ '--pc-as-accent': ph.accent }}>
                <div className="pc-as-phase-head">
                  <span className="pc-as-phase-no">Phase {idx + 1}</span>
                  <span className="pc-as-phase-title">{ph.label}</span>
                  <span className={`pc-as-phase-prog ${phaseDone ? 'is-ok' : ''}`}>{pc.done}/{pc.total}</span>
                </div>
                <div className="pc-as-steps">
                  {ph.steps.map((step) => {
                    const done = !!checklist[step.id];
                    return (
                      <label key={step.id} className={`pc-as-step ${done ? 'is-done' : ''} ${busyItem === step.id ? 'is-busy' : ''}`}>
                        <input
                          type="checkbox"
                          checked={done}
                          disabled={busyItem === step.id}
                          onChange={(e) => toggleStep(step.id, e.target.checked)}
                        />
                        <span className="pc-as-step-box" aria-hidden="true">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
                        </span>
                        <span className="pc-as-step-label">{step.title}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </motion.aside>
    </>
  );
};

export default Protocool;
